/**
 * client/net/cmdgen.ts — fixed 60 Hz usercmd generation [NET §7.2, ARCHITECTURE §4].
 * cmd n: serverTime = cmdTimeBase + floor(n*1000/60)  (msec 16/17), angles quantized to shorts.
 * OWNER: NETCODE agent.
 *
 * - Look input is applied to `viewangles` every render frame (Q3 CL_AdjustAngles) for minimum
 *   latency; each cmd samples the current angles [NET §7.2].
 * - `due()` runs a real-time accumulator: cmd n is due at startReal + n*1000/60. At most
 *   MAX_CMD_CATCHUP_PER_FRAME cmds per frame; after a longer stall (hidden tab, GC, debugger) the
 *   missed time is DROPPED by moving the origin, so the server never sees a burst (and the server's
 *   speed-hack guard would forfeit that time anyway).
 * - Edge-triggered taps (melee, jump, throw) are latched by the input module until its next
 *   cmdIntent() call consumes them, so a tap between two cmd slots is never lost.
 */
import { CMD_HZ, MAX_CMD_CATCHUP_PER_FRAME, cmdTimeOffset } from '@shared/constants';
import { angleToShort, shortToAngle, shortToSigned } from '@shared/math';
import { createUserCmd, type UserCmd } from '@shared/types';
import type { CmdIntent } from '../types';

export class CmdGenerator {
  /** Raw client view angles (Q3 cl.viewangles), degrees; the displayed view adds ps.deltaAngles. */
  readonly viewangles: [number, number, number] = [0, 0, 0];
  nextNumber = 0;
  private cmdTimeBase = 0;
  private startReal = -1;
  /** Cmd slots dropped after stalls (diagnostics). */
  dropped = 0;

  reset(cmdTimeBase: number): void {
    this.cmdTimeBase = cmdTimeBase;
    this.nextNumber = 0;
    this.startReal = -1;
  }

  get base(): number { return this.cmdTimeBase; }

  /** Apply look input every render frame (Q3 CL_AdjustAngles); clamps pitch against deltaAngles. */
  addLook(yawDelta: number, pitchDelta: number, deltaPitchShort: number, pitchUp: number, pitchDown: number): void {
    this.viewangles[1] += yawDelta || 0;
    this.viewangles[0] += pitchDelta || 0;
    const d = shortToAngle(shortToSigned(deltaPitchShort));
    if (this.viewangles[0] + d > pitchDown) this.viewangles[0] = pitchDown - d;
    if (this.viewangles[0] + d < -pitchUp) this.viewangles[0] = -pitchUp - d;
    this.viewangles[1] %= 360;
  }

  /** Number of cmd slots due at realNow (0..MAX_CMD_CATCHUP_PER_FRAME); drops time after stalls. */
  due(realNow: number): number {
    if (this.startReal < 0) this.startReal = realNow;
    const target = Math.floor(((realNow - this.startReal) * CMD_HZ) / 1000) + 1;
    let n = target - this.nextNumber;
    if (n > MAX_CMD_CATCHUP_PER_FRAME) {
      // stall: drop the missed time (shift the real-time origin) instead of bursting
      const drop = n - MAX_CMD_CATCHUP_PER_FRAME;
      this.startReal += (drop * 1000) / CMD_HZ;
      this.dropped += drop;
      n = MAX_CMD_CATCHUP_PER_FRAME;
    }
    return Math.max(0, n);
  }

  /**
   * Fraction of the way from the last generated cmd to the next one at realNow (0..1). The view
   * draws lerp(state after cmd n-1, state after cmd n, frac): smooth at any display rate.
   */
  frac(realNow: number): number {
    if (this.startReal < 0 || this.nextNumber === 0) return 1;
    const pos = ((realNow - this.startReal) * CMD_HZ) / 1000 - (this.nextNumber - 1);
    return pos < 0 ? 0 : pos > 1 ? 1 : pos;
  }

  /** Build the next cmd from an intent. */
  build(intent: CmdIntent, viewTime: number, kick?: readonly number[]): UserCmd {
    const c = createUserCmd();
    c.number = this.nextNumber++;
    c.serverTime = this.cmdTimeBase + cmdTimeOffset(c.number);
    c.buttons = intent.buttons >>> 0;
    // cmd angles = client view angles (+ transient fire kick, CONTRACT-REQUESTS #V1); quantized to
    // shorts here, so prediction runs on exactly what the server gets [NET pitfall 5]
    c.angles[0] = angleToShort(this.viewangles[0] + (kick ? kick[0] : 0));
    c.angles[1] = angleToShort(this.viewangles[1] + (kick ? kick[1] : 0));
    c.angles[2] = angleToShort(this.viewangles[2] + (kick ? kick[2] : 0));
    c.forwardmove = Math.max(-127, Math.min(127, Math.round(intent.forwardmove) || 0));
    c.rightmove = Math.max(-127, Math.min(127, Math.round(intent.rightmove) || 0));
    c.weapon = intent.weapon;
    c.viewTime = Math.floor(viewTime);
    c.meleeChargeYaw = intent.meleeChargeYaw & 0xffff;
    c.meleeChargeDist = Math.max(0, Math.min(255, Math.round(intent.meleeChargeDist) || 0));
    return c;
  }
}
